import React, { useState } from 'react';
import QuestionCard from './QuestionCard';
import InsightCard from './InsightCard';
import { getScoreColor, getSWOTCounts, calculateQuestionScore } from '../utils/evaluationScoreUtils';
import '../styles/components/SectionCard.css';

interface SWOTItem {
  id: number;
  finding: string;
  swot_type_id: number;
  section_id?: number;
}

interface Evidence {
  id: number;
  analysis_config_id: number;
  start_time: string;
  end_time: string;
  evidence: string;
}

interface MomentOperationInterface {
  id: number;
  name: string;
  description: string;
}

interface MomentItem {
  id: number;
  name: string;
  description?: string;
  category_id?: number;
}

interface SectionCardProps {
  id: number;
  title: string;
  points: number;
  totalPoints: number;
  questions: Array<{
    id: number;
    form_question: {
      description: string;
      type: string;
      option_type: string;
      form_options: any[];
      points: number;
      prompt: string;
      moments?: any[];
    };
    points: number;
    remarks: string;
    reviewer_points: number | null;
    is_reviewer_points: number;
    evidences?: Evidence[];
  }>;
  swotItems: SWOTItem[];
  evaluationId: string;
  searchText?: string;
  onEvidenceClick?: (startTime: string) => void;
  moments?: MomentItem[];
  operations?: MomentOperationInterface[];
  onRemarksChange: (questionId: string | number, remarks: string) => void;
  isEditable?: boolean;
  onEvaluationUpdate?: () => void;
  onSectionScoreChange?: (sectionId: number, newScore: number) => void;
  // Global editing state
  currentlyEditingQuestion?: { sectionId: number; questionId: number } | null;
  hasPendingChanges?: boolean;
  reasonText?: string;
  pendingSelections?: Record<number, boolean>;
  onPendingSelectionsChange?: (sectionId: number, questionId: number, selections: Record<number, boolean>) => void;
  onHasPendingChangesChange?: (sectionId: number, questionId: number, hasChanges: boolean) => void;
  onReasonTextChange?: (sectionId: number, questionId: number, reason: string) => void;
  onRequestEditPermission?: (sectionId: number, questionId: number) => boolean;
  onReleaseEditPermission?: (sectionId: number, questionId: number) => void;
  onResetPendingChanges?: () => void;
}

const SectionCard: React.FC<SectionCardProps> = ({
  id,
  title,
  points,
  totalPoints,
  questions,
  swotItems,
  evaluationId,
  searchText,
  onEvidenceClick,
  moments = [],
  operations = [],
  onRemarksChange,
  isEditable = false,
  onEvaluationUpdate,
  onSectionScoreChange,
  // Global editing state
  currentlyEditingQuestion,
  hasPendingChanges = false,
  reasonText = '',
  pendingSelections = {},
  onPendingSelectionsChange,
  onHasPendingChangesChange,
  onReasonTextChange,
  onRequestEditPermission,
  onReleaseEditPermission,
  onResetPendingChanges
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [questionScores, setQuestionScores] = useState<Record<number, number>>({});
  
  // Global editing state is now managed by EvaluationPanel

  // Function to get question score considering reviewer overrides
  const getQuestionScore = (question: any): number => {
    if (question.is_reviewer_points === 1 && question.reviewer_points !== null) {
      return question.reviewer_points; // Reviewer override score
    }
    
    return question.points; // Use original server score
  };

  // Initialize question scores from server data
  React.useEffect(() => {
    const initialScores: Record<number, number> = {};
    questions.forEach(question => {
      initialScores[question.id] = getQuestionScore(question);
    });
    setQuestionScores(initialScores);
  }, [questions]);

  // Handle question score changes
  const handleQuestionScoreChange = (questionId: string | number, newScore: number) => {
    setQuestionScores(prev => ({
      ...prev,
      [questionId]: newScore
    }));
  };

  // Global editing handlers are now managed by EvaluationPanel


  // Local state for question scores to prevent flicker
  const [localQuestionScores, setLocalQuestionScores] = React.useState<Record<number, number>>({});

  // Initialize local scores from server data
  React.useEffect(() => {
    const serverScores: Record<number, number> = {};
    questions.forEach(question => {
      serverScores[question.id] = getQuestionScore(question);
    });
    setLocalQuestionScores(serverScores);
  }, [questions]);

  // Calculate current section score using local scores
  const currentSectionScore = Object.values(localQuestionScores).reduce((total, score) => total + score, 0);

  // Update local scores when selections change
  React.useEffect(() => {
    if (currentlyEditingQuestion?.sectionId === id && 
        Object.keys(pendingSelections).length > 0) {
      const editingQuestionId = currentlyEditingQuestion.questionId;
      const question = questions.find(q => parseInt(q.id.toString()) === editingQuestionId);
      
      if (question) {
        // Update only the editing question's score
        setLocalQuestionScores(prev => ({
          ...prev,
          [question.id]: calculateQuestionScore(
            question.form_question.form_options || [], 
            pendingSelections
          )
        }));
      }
    }
  }, [pendingSelections, hasPendingChanges, currentlyEditingQuestion, id]);

  // Notify parent component when section score changes
  React.useEffect(() => {
    if (onSectionScoreChange) {
      onSectionScoreChange(id, currentSectionScore);
    }
  }, [currentSectionScore, id, onSectionScoreChange]);

  const highlightText = (text: string) => {
    if (!searchText || !text) return text;
    
    const parts = text.split(new RegExp(`(${searchText})`, 'gi'));
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === searchText.toLowerCase()) {
        return <mark key={index} className="search-highlight">{part}</mark>;
      }
      return part;
    });
  };

  return (
    <div className="section-card">
      <div 
        className="section-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="section-card-title">
          <h3>{highlightText(title)}</h3>
        </div>
        {(() => {
          const counts = getSWOTCounts(swotItems);
          const hasAnyCounts = counts.strength > 0 || counts.opportunity > 0 || counts.weakness > 0 || counts.threat > 0;
          
          return hasAnyCounts ? (
            <div className="section-card-swot-counts">
              <span className="swot-label">SWOT</span>
              {counts.strength > 0 && (
                <span className="swot-count-badge theme-success">
                  {counts.strength}
                </span>
              )}
              {counts.opportunity > 0 && (
                <span className="swot-count-badge theme-warning">
                  {counts.opportunity}
                </span>
              )}
              {counts.weakness > 0 && (
                <span className="swot-count-badge theme-danger">
                  {counts.weakness}
                </span>
              )}
              {counts.threat > 0 && (
                <span className="swot-count-badge theme-danger">
                  {counts.threat}
                </span>
              )}
            </div>
          ) : null;
        })()}
        <div className={`section-card-score ${getScoreColor(currentSectionScore, totalPoints)}`}>
          {`${currentSectionScore} / ${totalPoints || 0}`}
        </div>
        <span className="material-icons">
          {isExpanded ? 'expand_less' : 'expand_more'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="section-card-content">
          {questions.map((question, index) => {
            const commonProps = {
              id: question.id,
              questionNumber: index + 1,
              questionText: question.form_question.description,
              questionType: question.form_question.type,
              optionType: question.form_question.option_type,
              options: question.form_question.form_options,
              points: question.form_question.points,
              evaluationId: evaluationId,
              evidences: question.evidences,
              onEvidenceClick: onEvidenceClick,
              prompt: question.form_question.prompt,
              moments: question.form_question.type === 'Moment' ? 
                [...(question.form_question.moments || [])]
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map(moment => {
                    const momentDetails = moments.find(m => m.id === moment.moment_id);
                    const operationDetails = operations.find(o => o.id === moment.operation_id);
                  
                    return {
                      id: moment.moment_id,
                      name: momentDetails?.name || 'Undefined Moment',
                      operation: operationDetails?.name || 'NONE'
                    };
                  })
                : undefined
            };

            return (
              <QuestionCard
                key={question.id}
                {...commonProps}
                remarks={question.remarks}
                onRemarksChange={onRemarksChange}
                isEditable={isEditable}
                onEvaluationUpdate={onEvaluationUpdate}
                // Global editing props - pass state if this question is being edited
                pendingSelections={currentlyEditingQuestion?.sectionId === id && currentlyEditingQuestion?.questionId === parseInt(question.id.toString()) ? pendingSelections : {}}
                hasPendingChanges={currentlyEditingQuestion?.sectionId === id && currentlyEditingQuestion?.questionId === parseInt(question.id.toString()) ? hasPendingChanges : false}
                reasonText={currentlyEditingQuestion?.sectionId === id && currentlyEditingQuestion?.questionId === parseInt(question.id.toString()) ? reasonText : ''}
                onPendingSelectionsChange={(questionId, selections) => onPendingSelectionsChange?.(id, parseInt(questionId.toString()), selections)}
                onHasPendingChangesChange={(questionId, hasChanges) => onHasPendingChangesChange?.(id, parseInt(questionId.toString()), hasChanges)}
                onReasonTextChange={(questionId, reason) => onReasonTextChange?.(id, parseInt(questionId.toString()), reason)}
                onResetPendingChanges={onResetPendingChanges}
                onRequestEditPermission={(questionId) => onRequestEditPermission?.(id, parseInt(questionId.toString())) || false}
                onReleaseEditPermission={(questionId) => onReleaseEditPermission?.(id, parseInt(questionId.toString()))}
              />
            );
          })}
          
          {swotItems && swotItems.length > 0 && (
            <div className="s-contentsection-insight">
              <InsightCard swotItems={swotItems} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionCard;
